import { isEqual, omit } from "lodash";
import { useCallback, useReducer } from "react";
import { ViewOptionStateEnum } from "../../components/contextual-app-bar/ViewOptionStateEnum";
import { actionItems } from "./GroupManualEntryActions";
import { ActionMenuItem } from "../../components/action-menu/ActionMenuItem";
import { sortAndDispatchActions } from "../../common/utilities/SortAndDispatchActions";

export type GroupManualEntryStateType = {
    viewOptionState: ViewOptionStateEnum;
    actions: ActionMenuItem[];
    date: Date;
    downloadUid: string | undefined;
    isDirty: boolean;
};

const defaultGroupManualEntryState: GroupManualEntryStateType = {
    viewOptionState: ViewOptionStateEnum.Default,
    actions: actionItems,
    date: new Date(),
    downloadUid: undefined,
    isDirty: false
};

enum GroupManualEntryActionType {
    ActionsChanged = 'ACTIONS_CHANGED',
    DateChanged = 'DATE_CHANGED',
    DownloadUidChanged = 'DOWNLOAD_UID_CHANGED',
    Reset = 'RESET', 
}

type GroupManualEntryStateActions =  {
    type: GroupManualEntryActionType.ActionsChanged;
    payload: ActionMenuItem[];
} | {
    type: GroupManualEntryActionType.DateChanged;
    date: Date;
} | {
    type: GroupManualEntryActionType.DownloadUidChanged;
    payload: string | undefined;
} | {
    type: GroupManualEntryActionType.Reset;
    payload: void;
};

function groupManualEntryStateReducer(state: GroupManualEntryStateType, action: GroupManualEntryStateActions): GroupManualEntryStateType {
    const copy = { ...state };
    switch(action.type) {
        
        case GroupManualEntryActionType.ActionsChanged:
            return {...state,
                actions: action.payload //.sort((left, right) => (left.order ?? 0) - (right.order ?? 0))
            };

        case GroupManualEntryActionType.DateChanged:
            copy.date = action.date;
            copy.isDirty = !isEqual(omit(copy, 'isDirty'), omit(defaultGroupManualEntryState, 'isDirty'));
            copy.viewOptionState = copy.isDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;

        case GroupManualEntryActionType.DownloadUidChanged:
            return {
                ...state,
                downloadUid: action.payload
            };

        case GroupManualEntryActionType.Reset:
            return {
                ...defaultGroupManualEntryState,
                date: new Date(),
            };

        default:
            return state;
    }
}

export default function useGroupManualEntryState() {
    const [state, dispatch] = useReducer(groupManualEntryStateReducer, defaultGroupManualEntryState);

    // const setActions = useCallback((value: ActionMenuItem[]) => dispatch({
    //     type: GroupManualEntryActionType.ActionsChanged,
    //     payload: value
    // }), []);

    const setActions = useCallback((actions: ActionMenuItem[]) => sortAndDispatchActions(
        state.actions,
        actions,
        (sortedActions) => dispatch({
            type: GroupManualEntryActionType.ActionsChanged,
            payload: sortedActions
        })
    ), [state.actions]);

    const setDate = useCallback((date: Date) => dispatch({
        type: GroupManualEntryActionType.DateChanged,
        date: date
    }), []);

    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
        type: GroupManualEntryActionType.DownloadUidChanged,
        payload: value
    }), []);

    const resetState = useCallback(() => dispatch({
        type: GroupManualEntryActionType.Reset,
        payload: undefined
    }), []);

    return {
        state,
        setActions,
        setDate,
        setDownloadUid,
        resetState
    }
}